import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useIntl } from 'umi';

import DataCreator from '@/components/Common/DataCreator';
import UserPicture from '@/components/Common/UserPicture';

import styles from './style.less';
import { HERO_DETAIL_AUDIT_ROWS, isEmptyDetailValue, resolveHeroDetailAuditUser } from './utils';

/**
 * Audit metadata block — rendered above details footer actions.
 */
const HeroDetailsAudit = ({ data, sourceItem, showNull, className }) => {
  const { formatMessage } = useIntl();

  const isVisible = (value) => !isEmptyDetailValue(value) || showNull;
  const byName = Object.fromEntries((data || []).map((field) => [field.name, field]));

  const cards = HERO_DETAIL_AUDIT_ROWS.map(
    ({ labelId, dateOnlyLabelId, userKeys, dateKey, hideWhenEmptyUser }) => {
      const userField = userKeys.map((key) => byName[key]).find(Boolean);
      const dateField = byName[dateKey];
      const user = resolveHeroDetailAuditUser(sourceItem, userKeys, userField?.value);
      const date = dateField?.value;
      const hasUser = !!(user.fullName || user.dataCreator);

      if (hideWhenEmptyUser && !hasUser) return null;

      if (!hasUser && !isVisible(date)) return null;

      const avatar = user.dataCreator ? (
        <DataCreator {...user.dataCreator} iconOnly size={32} />
      ) : user.fullName ? (
        <UserPicture
          id={user.id}
          fullName={user.fullName}
          size={32}
          shape="circle"
          showTooltip={false}
        />
      ) : null;

      const resolvedLabelId = !hasUser && dateOnlyLabelId ? dateOnlyLabelId : labelId;

      return (
        <div key={userKeys.join('-')} className={styles.auditKpi}>
          <span className={styles.auditKpiLabel}>{formatMessage({ id: resolvedLabelId })}</span>
          <div className={styles.auditKpiBody}>
            {avatar}
            <div className={styles.auditUserText}>
              {user.fullName ? <span className={styles.auditUserName}>{user.fullName}</span> : null}
              {isVisible(date) ? <span className={styles.auditDate}>{date}</span> : null}
            </div>
          </div>
        </div>
      );
    },
  ).filter(Boolean);

  if (!cards.length) return null;

  return (
    <aside className={classNames(styles.audit, className)}>
      <div className={classNames(styles.auditGrid, cards.length === 1 && styles.auditGridSingle)}>
        {cards}
      </div>
    </aside>
  );
};

HeroDetailsAudit.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  sourceItem: PropTypes.object,
  showNull: PropTypes.bool,
  className: PropTypes.string,
};

export default HeroDetailsAudit;
